import type { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'

/**
 * Extracts text before the first colon from a string.
 * Used to extract PT name from paragraph text like "PT Name: content"
 *
 * @param text - The text to extract from
 * @returns The text before the first colon, trimmed
 *
 * @example
 * extractTextBeforeColon("Concrete: Type A") // Returns "Concrete"
 * extractTextBeforeColon("No colon here") // Returns "No colon here"
 */
export const extractTextBeforeColon = (text: string): string => {
    if (!text) return ''

    const colonIndex = text.indexOf(':')
    if (colonIndex === -1) {
        return text.trim()
    }

    return text.substring(0, colonIndex).trim()
}

/**
 * Checks if a paragraph is linked to an inactive Product Type.
 *
 * Logic:
 * 1. Extract PT name from paragraph text (text before colon)
 * 2. Find matching PT element node by name
 * 3. Check if PT exists but IsInProject = false
 *
 * @param paragraphText - The paragraph text (e.g., "Concrete: Type A")
 * @param ptElementNodes - All PT element nodes for the current family
 * @returns Object with hasInactivePT flag and PT element node if found
 *
 * @example
 * const ptNodes = [
 *   { text: "Concrete", elementType: "PRODUCTTYPE", isInProject: false },
 *   { text: "Steel", elementType: "PRODUCTTYPE", isInProject: true }
 * ]
 * checkParagraphLinkedToInactivePT("Concrete: Type A", ptNodes)
 * // Returns { hasInactivePT: true, ptElementNode: {...}, ptName: "Concrete" }
 */
export const checkParagraphLinkedToInactivePT = (
    paragraphText: string | undefined,
    ptElementNodes: ProjectElementNode[]
): {
    hasInactivePT: boolean
    ptElementNode?: ProjectElementNode
    ptName?: string
} => {
    if (!paragraphText || !paragraphText.includes(':')) {
        return { hasInactivePT: false }
    }

    // Extract PT name from paragraph (text before colon)
    const ptName = extractTextBeforeColon(paragraphText)
    if (!ptName) {
        return { hasInactivePT: false }
    }

    // Find matching PT element node (case-insensitive)
    const matchingPT = ptElementNodes.find((node) => {
        if (node.elementType !== 'PRODUCTTYPE') return false

        const nodePTName = extractTextBeforeColon(node.text || '')
        return nodePTName.toLowerCase() === ptName.toLowerCase()
    })

    if (!matchingPT) {
        return { hasInactivePT: false }
    }

    // Check if PT exists but is not in project
    if (!matchingPT.isInProject) {
        return {
            hasInactivePT: true,
            ptElementNode: matchingPT,
            ptName: ptName,
        }
    }

    return { hasInactivePT: false }
}

/**
 * Gets all PT element nodes for a given family node ID.
 * Filters element nodes to only return ProductType nodes.
 *
 * @param allElementNodes - All element nodes under the family
 * @returns Array of PT element nodes only
 */
export const getPTElementNodes = (
    allElementNodes: ProjectElementNode[]
): ProjectElementNode[] => {
    return allElementNodes.filter((node) => node.elementType === 'PRODUCTTYPE')
}

/**
 * Gets the display name for a Product Type element node.
 * Extracts text before colon from node.text
 *
 * @param ptNode - The PT element node
 * @returns Display name for the PT
 */
export const getPTDisplayName = (ptNode: ProjectElementNode): string => {
    return extractTextBeforeColon(ptNode.text || 'Unknown Product Type')
}

/**
 * Builds a combined list of PT element nodes from elementList and cached PTs,
 * deduplicates them by PT id, and returns only PRODUCTTYPE nodes.
 *
 * @param elementList - The current element list (may be filtered by "Added To Project")
 * @param cachedPTs - Array of cached PT nodes to include
 * @returns Deduplicated array of PT element nodes
 *
 * @example
 * const ptNodes = buildCombinedPTElementNodes(elementList, [cachedPT])
 */
export const buildCombinedPTElementNodes = (
    elementList: ProjectElementNode[],
    cachedPTs: ProjectElementNode[]
): ProjectElementNode[] => {
    // Build combined element list
    const combinedElementList =
        cachedPTs.length > 0 ? [...elementList, ...cachedPTs] : elementList

    // Deduplicate by PT id
    const uniqueElements = Array.from(
        new Map(combinedElementList.map((el) => [el.id, el])).values()
    )

    // Return PT element nodes only
    return getPTElementNodes(uniqueElements)
}

/**
 * Finds an inactive PT element node from a content item's text.
 * Used by PT detection functions to identify inactive PTs in content.
 *
 * @param contentItem - The content item to check
 * @param ptElementNodes - Array of PT element nodes to search
 * @returns The inactive PT element node if found, null otherwise
 *
 * @example
 * const inactivePT = findInactivePTFromContentItem(childItem, ptElementNodes)
 * if (inactivePT) {
 *     inactivePTMap.set(inactivePT.id, inactivePT)
 * }
 */
export const findInactivePTFromContentItem = (
    contentItem: Partial<{ elements?: Array<{ text?: string }>; textType?: string }>,
    ptElementNodes: ProjectElementNode[]
): ProjectElementNode | null => {
    const itemText = contentItem.elements?.[0]?.text

    // Must have text with colon
    if (!itemText?.includes(':')) {
        return null
    }

    // Extract PT name from text (text before colon)
    const ptName = extractTextBeforeColon(itemText)
    if (!ptName) {
        return null
    }

    // Find matching PT in ptElementNodes array (case-insensitive)
    const matchingPT = ptElementNodes.find((pt) => {
        const ptDisplayName = extractTextBeforeColon(pt.text || '')
        return ptDisplayName.toLowerCase() === ptName.toLowerCase()
    })

    // Return PT only if it exists, is inactive (not in project), and has id
    if (matchingPT && !matchingPT.isInProject && matchingPT.id) {
        return matchingPT
    }

    return null
}
